import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "@/api/query-keys";
import type { PaginatedResponse } from "@/api/types";
import { api } from "@/lib/api";
import type {
	DeploymentReportListParams,
	DeploymentReportRow,
	DetectedMeter,
	MediaTransmitter,
	MediaTransmittersListParams,
} from "./types";

const LIST_URL = "/api/districts/deployment_tool/sources/";
const DETECTED_METERS_URL = (sn: string) =>
	`/api/districts/deployment_tool/sources/${sn}/`;
const REPORT_URL = "/api/districts/deployment_tool/report/";

export function useMediaTransmitters(params: MediaTransmittersListParams) {
	return useQuery({
		queryKey: queryKeys.mediaTransmitters.list(params),
		queryFn: async () => {
			const res = await api.get<PaginatedResponse<MediaTransmitter>>(LIST_URL, {
				params,
			});
			return res.data;
		},
	});
}

export function useDetectedMeters(
	sn: string,
	params: MediaTransmittersListParams,
) {
	return useQuery({
		queryKey: queryKeys.mediaTransmitters.detectedMeters(sn, params),
		queryFn: async () => {
			const res = await api.get<PaginatedResponse<DetectedMeter>>(
				DETECTED_METERS_URL(sn),
				{ params },
			);
			return res.data;
		},
		enabled: Boolean(sn),
	});
}

export function useDeploymentReport(params: DeploymentReportListParams) {
	return useQuery({
		queryKey: queryKeys.mediaTransmitters.report(params),
		queryFn: async () => {
			const res = await api.get<PaginatedResponse<DeploymentReportRow>>(
				REPORT_URL,
				{ params },
			);
			return res.data;
		},
	});
}

export async function downloadDeploymentReportCsv(
	params: Omit<DeploymentReportListParams, "page" | "page_size">,
): Promise<void> {
	const res = await api.get(REPORT_URL, {
		params: { ...params, export: "csv" },
		responseType: "blob",
	});
	const url = URL.createObjectURL(res.data as Blob);
	const a = document.createElement("a");
	a.href = url;
	const today = new Date().toISOString().slice(0, 10);
	a.download = `deployment-report-${today}.csv`;
	const disposition = res.headers["content-disposition"] as string | undefined;
	if (disposition) {
		const match = disposition.match(/filename="?([^";\n]+)"?/);
		if (match?.[1]) a.download = match[1];
	}
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
	URL.revokeObjectURL(url);
}
